import * as React from 'react'
import { Text, View, ScrollView } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import TextInput from '@components/textInput/TextInput'
import LoginLayout from '@screens/login/LoginLayout'
import Loader from '@components/loader/Loader'
import Button from '@components/button/Button'
import SimpleAlert from '@components/modal/SimpleAlert'

import { FormikHelpers, useFormik } from 'formik'
import * as yup from 'yup'

import { reset } from '@store/store'

import api from '@api/api'
import Logger from '@utils/logger'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'

interface Props {
  token: string
}

interface Values {
  password: string
  confirmedPassword: string
}

const initialValues: Values = { password: '', confirmedPassword: '' }

const validationSchema = yup.object().shape({
  password: yup.string().required('errors.form.empty'),
  confirmedPassword: yup
    .string()
    .required('errors.form.empty')
    .oneOf([yup.ref('password'), null], 'screens.resetPassword.errors.match'),
})

const ResetPasswordScreen = ({ token }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()

  const [status, setStatus] = React.useState<Status | 'success'>('loading')
  const [secured, setSecured] = React.useState(true)
  const [confirmedSecured, setConfirmedSecured] = React.useState(true)

  React.useEffect(() => {
    // On déconnecte l'utilisateur s'il y accède sur une session où il est connecté
    reset()
    if (!token) {
      setStatus('error')
    } else {
      api.authentification
        .verifyToken({ token })
        .then(() => setStatus('fetched'))
        .catch((err) => {
          Logger.error(err)
          setStatus('error')
        })
    }
  }, [])

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    setSubmitting(true)

    api.authentification
      .resetPassword({ token, password: vals.password })
      .then(() => setStatus('success'))
      .catch((err) => {
        Logger.error(err)
        SimpleAlert.open({
          title: i18n.t('common.errorTitle'),
          description: i18n.t('common.error'),
          buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
        })
      })
      .finally(() => setSubmitting(false))
  }

  const { values, handleChange, errors, handleBlur, handleSubmit, isValid, isSubmitting, touched } = useFormik<Values>({
    initialValues,
    onSubmit,
    validationSchema,
  })

  const renderLoader = () => (
    <LoaderContainer>
      <Loader color={theme.colors.primary} />
    </LoaderContainer>
  )

  const renderContent = () => {
    if (status === 'error' || status === 'success') {
      return (
        <>
          <Title>{i18n.t(`screens.resetPassword.${status}.title`)}</Title>
          <Description>{i18n.t(`screens.resetPassword.${status}.description`)}</Description>
          <ButtonContainer>
            <Button status="active" onClick={navigation.reset} label={i18n.t('common.return')} />
          </ButtonContainer>
        </>
      )
    }

    return (
      <>
        <Title>{i18n.t('screens.resetPassword.form.title')}</Title>
        <Description>{i18n.t('screens.resetPassword.form.description')}</Description>
        <TextInputContainer>
          <InputLabel>{i18n.t('screens.resetPassword.fields.password')}</InputLabel>
          <TextInput
            value={values.password}
            onChange={handleChange('password')}
            title={i18n.t('screens.resetPassword.fields.password')}
            placeholder={i18n.t('screens.resetPassword.fields.password')}
            autoCapitalize="none"
            right={{
              name: secured ? 'visibility' : 'unvisibility',
              onClick: () => setSecured(!secured),
            }}
            error={touched.password && errors.password ? i18n.t(errors.password) : undefined}
            secureTextEntry={secured}
            returnKeyType="next"
            onBlur={handleBlur('password')}
          />
        </TextInputContainer>
        <TextInputContainer>
          <InputLabel>{i18n.t('screens.resetPassword.fields.confirmedPassword')}</InputLabel>
          <TextInput
            value={values.confirmedPassword}
            onChange={handleChange('confirmedPassword')}
            title={i18n.t('screens.resetPassword.fields.confirmedPassword')}
            placeholder={i18n.t('screens.resetPassword.fields.confirmedPassword')}
            autoCapitalize="none"
            right={{
              name: confirmedSecured ? 'visibility' : 'unvisibility',
              onClick: () => setConfirmedSecured(!confirmedSecured),
            }}
            error={touched.confirmedPassword && errors.confirmedPassword ? i18n.t(errors.confirmedPassword) : undefined}
            secureTextEntry={confirmedSecured}
            returnKeyType="send"
            onBlur={handleBlur('confirmedPassword')}
          />
        </TextInputContainer>
        <ButtonContainer>
          <ButtonContainer>
            <Button
              status={isSubmitting ? 'loading' : !isValid ? 'disabled' : 'active'}
              onClick={handleSubmit}
              label={i18n.t('common.save')}
            />
          </ButtonContainer>
        </ButtonContainer>
      </>
    )
  }

  return (
    <LoginLayout>
      {status === 'loading' ? (
        renderLoader()
      ) : (
        <ScrollContainer>
          <ContentContainer>{renderContent()}</ContentContainer>
        </ScrollContainer>
      )}
    </LoginLayout>
  )
}

export default ResetPasswordScreen

const ScrollContainer = styled(ScrollView)`
  flex: 1;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Heading1};
  margin-bottom: 36px;
`

const LoaderContainer = styled(View)`
  flex: 1;
  align-items: center;
  justify-content: center;
`

const ContentContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '32px 24px 120px;' : '132px 100px;')};
`

const Description = styled(Text)`
  ${(props) => props.theme.fonts.Title2};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-bottom: 32px;
`

const ButtonContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && `max-width: 280px;`}
  margin-top: 32px;
`

const TextInputContainer = styled(View)`
  margin-bottom: 24px;
`

const InputLabel = styled(Text)`
  ${(props) => props.theme.fonts.Option};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-bottom: 4px;
  ${(props) => props.theme.breakpoint === 'phone' && `display: none;`}
`
